/**
 * Live Events Feed — cricket + esports + OTT release calendar.
 * Data comes from /api/games/events (backend refreshes cache daily).
 * All sources have permissive/CC licenses — attribution shown at the bottom.
 */
import { useState, useEffect } from 'react';
import { Trophy, Goal, Gamepad2, Tv, Calendar, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'cricket',  label: 'Cricket',  icon: Trophy,   color: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'football', label: 'Football', icon: Goal,     color: 'text-sky-600 dark:text-sky-400' },
  { key: 'esports',  label: 'Esports',  icon: Gamepad2, color: 'text-purple-600 dark:text-purple-400' },
  { key: 'ott',      label: 'OTT & TV', icon: Tv,       color: 'text-amber-600 dark:text-yellow-400' },
];

function fmtWhen(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const days = Math.floor((d - now) / 86400000);
    const dstr = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    const tstr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    if (days < 0) return `${dstr}, ${tstr} (past)`;
    return `${dstr}, ${tstr}`;
  } catch { return ''; }
}

export default function EventsFeed() {
  const [tab, setTab] = useState('cricket');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/api/games/events`, { params: { category: tab, limit: 12 } })
      .then(r => { if (alive) setItems(r.data.events || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tab]);

  return (
    <section className="py-12 border-t border-slate-200 dark:border-white/5 bg-gradient-to-b from-slate-50 dark:from-[#0F172A] to-white dark:to-[#0B1220]" data-testid="events-feed">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-amber-600 dark:text-yellow-400" />
              <span className="text-xs font-bold text-amber-600 dark:text-yellow-400 uppercase tracking-widest">Watch &amp; Play · This Week</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">What fans are doing this week</h2>
            <p className="text-sm text-slate-600 dark:text-gray-300 mt-1">Cricket &amp; football fixtures, esports tournaments, and new episodes hitting streaming. Auto-refreshed every few hours so it's always current.</p>
          </div>
          <div className="flex flex-wrap gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-lg border border-slate-200 dark:border-white/10 max-w-full">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors flex items-center gap-1 ${
                  tab === t.key ? 'bg-slate-100 dark:bg-white/10 text-slate-900 dark:text-white' : 'text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white'
                }`}
                data-testid={`events-tab-${t.key}`}
              >
                <t.icon className={`w-3.5 h-3.5 ${tab === t.key ? t.color : ''}`} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-600 dark:text-gray-300 text-sm" data-testid="events-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading schedule…
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-slate-600 dark:text-gray-300 text-sm" data-testid="events-empty">
            No upcoming {tab === 'ott' ? 'releases' : 'matches'} in the feed right now, check back tomorrow.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="events-list">
            {items.map((e, i) => (
              <a
                key={i}
                href={e.source_url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-slate-100 dark:bg-white/5 hover:bg-slate-100 dark:bg-white/[0.08] border border-slate-200 dark:border-white/5 hover:border-yellow-400/30 rounded-xl p-4 transition-all"
                data-testid={`event-card-${i}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-gray-300">
                    {e.league_name || e.league || tab}
                  </span>
                  <ExternalLink className="w-3 h-3 text-slate-500 dark:text-gray-400 group-hover:text-amber-600 dark:text-yellow-400 transition-colors shrink-0" />
                </div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white leading-snug line-clamp-2 mb-2">
                  {e.title}
                </h3>
                <div className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-emerald-600 dark:text-emerald-300 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {fmtWhen(e.start_at)}
                  </span>
                  {e.venue && (
                    <span className="text-slate-600 dark:text-gray-300 truncate max-w-[45%]" title={e.venue}>
                      {e.venue}
                    </span>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Attribution, required by TheSportsDB, PandaScore, TVMaze free tiers */}
        <p className="text-[10px] text-slate-500 dark:text-gray-400 mt-6 text-center">
          Data:{' '}
          <a href="https://www.thesportsdb.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600 dark:text-gray-300">TheSportsDB</a>
          {' · '}
          <a href="https://pandascore.co/" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600 dark:text-gray-300">PandaScore</a>
          {' · '}
          <a href="https://www.tvmaze.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600 dark:text-gray-300">TVmaze</a>
        </p>
      </div>
    </section>
  );
}
